import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule, RouteReuseStrategy } from '@angular/router';
import { User } from '@auth0/auth0-angular';
import {
  interval,
  Observable,
  Subscription,
  switchMap,
} from 'rxjs';
import { TypeOfResult } from 'src/app/models/typeOfResult/typeOfResult';
import { Role } from 'src/app/models/user/role.model';
import { AdministratorService } from 'src/app/services/administrator.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { IndustriesService } from 'src/app/services/industries.service';
import { LenderOrganizationsService } from 'src/app/services/lender.organizations.service';
import { NotificationService } from 'src/app/services/notifications.service';
import { BulkDownloadService } from 'src/app/services/bulk-download.service';
import { ProductsService } from 'src/app/services/products.service';
import { ProviderOrganizationsService } from 'src/app/services/provider.organizations.service';
import { StatesService } from 'src/app/services/states.service';
import { IUserProfile, UsersService } from 'src/app/services/users.service';
import { SearchService } from '../../../../services/search.service';
import { ImageRendererComponent } from '../image-renderer/image-renderer.component';
import { GeneralSearchFormComponent } from './general-search/general-search-form.component';
import { MatMenuModule } from '@angular/material/menu';
import { NotificationMenuTitleComponent } from './notification-menu-title/notification-menu-title';
import { NotificationMenuItemComponent } from './notification-menu-item/notification-menu-item';
import { CommonModule } from '@angular/common';
import { InitialsPipe } from '../../pipes/initials.pipe';
import { UtcToLocalPipe } from '../../pipes/utcToLocal.pipe';
import { AvatarProfileComponent } from '../avatar-profile/avatar-profile.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { computed, effect } from '@angular/core';
import { IProviderOrganizationRelationships } from 'src/app/models/provider/provider-organization-relationships.model';
import { ConsultantsService } from 'src/app/services/consultants.service';
import { ALL_RECORD_TYPES, filterRecordTypesByRole } from 'src/app/models/recordType/record-type.constants';

@Component({
    selector: 'app-main-navigation',
    templateUrl: './main-navigation.component.html',
    styleUrls: ['./main-navigation.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      RouterModule,
      ImageRendererComponent,
      GeneralSearchFormComponent,
      MatMenuModule,
      NotificationMenuTitleComponent,
      NotificationMenuItemComponent,
      InitialsPipe,
      UtcToLocalPipe,
      AvatarProfileComponent,
      MatTooltipModule
    ]
})
export class MainNavigationComponent implements OnInit {
  user$: Observable<User | null | undefined>;
  userProfile$: Observable<IUserProfile | null>;
  userRole: Role | undefined;
  userOrganization: string | undefined;
  notifications: any[] = [];

  filterGroup = new FormGroup({
    searchKey: new FormControl('', [Validators.minLength(4)]),
    statesIds: new FormControl(),
    lenderIds: new FormControl(),
    status: new FormControl(),
    submissionProgressId: new FormControl(),
    providerOrganizationId: new FormControl(),
    administratorId: new FormControl(),
    productIds: new FormControl(),
    industryIds: new FormControl(),
    formStatusId: new FormControl(),
    recordTypeIds: new FormControl(),
  });

  // Forms Management access is only known after a search response returns; default to
  // true (optimistic) so the filter shows all role-eligible record types until then,
  // then refine once SearchService reports the real value from the search response.
  hasFormsManagementAccess = true;

  get recordTypesList(): any[] {
    return filterRecordTypesByRole(ALL_RECORD_TYPES, this.userRole, this.hasFormsManagementAccess);
  }

  private subscription: Subscription;

  statesList: any[] = [];
  providerList: any[] = [];
  administratorList: any[] = [];
  industryList: any[] = [];
  productList: any[] = [];
  lendersList: any[] = [];
  originalLendersList?: any[] = [];
  showLendersDropdown: boolean = false;
  hasNextPageLenders = true;
  currentPageLenders = 1;
  hasNextPageProviders = true;
  currentPageProviders = 1;
  hasNextPageAdministrators = true;
  currentPageAdministrators = 1;
  pageSize = 20;

  readonly userRoleSignal = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly organizationIdSignal = toSignal(this.userService.organizationId$, { initialValue: undefined });
  readonly isProvider = computed(() => this.userRoleSignal() === Role.Provider);
  readonly isLender = computed(() => this.userRoleSignal() === Role.Lender);
  readonly isConsultant = computed(() => this.userRoleSignal() === Role.Consultant);

  readonly _loadProviderOrganizationsEffect = effect(() => {
    const role = this.userRoleSignal();
    const orgId = this.organizationIdSignal();

    if ((role === Role.Provider || role === Role.ProviderReadOnly) && orgId) {
      this.providerService.getRelationships(orgId.toString()).subscribe((response) => {
        if (response.isError) {
          return;
        }

        const data = response.data as IProviderOrganizationRelationships;

        if (data.parent !== null) {
          this.providerList = [
            { id: data.currentOrganization.id, name: data.currentOrganization.name },
          ];
          this.hasNextPageProviders = false;
        } else if (data.children.length > 0) {
          this.providerList = [
            { id: 0, name: 'All' },
            { id: data.currentOrganization.id, name: data.currentOrganization.name },
            ...data.children.map(child => ({ id: child.id, name: child.name }))
          ];
          this.hasNextPageProviders = false;
        } else {
          this.providerList = [
            { id: data.currentOrganization.id, name: data.currentOrganization.name },
          ];
          this.hasNextPageProviders = false;
        }
      });
    }
    else if (role === Role.Consultant) {
      this.consultantsService.getCurrentConsultantAssignedOrganizations().subscribe((response) => {
        if (response.isError) {
          this.providerList = [];
          this.hasNextPageProviders = false;
          return;
        }
        
        this.providerList = [
          { id: 0, name: 'All' },
          ...response.data.items.map((org: any) => ({ id: org.id, name: org.name }))
        ];
        this.hasNextPageProviders = false;
      });
    }
  });
readonly _loadLenderOrganizationsEffect = effect(() => {
  const role = this.userRoleSignal();
  const orgId = this.organizationIdSignal();

  if ((role === Role.Lender || role === Role.LenderReadOnly) && orgId) {
    this.lendersService.getRelationships(orgId.toString()).subscribe((response) => {
      if (response.isError) {
        this.showLendersDropdown = false;
        return;
      }
      const data = response.data as any;
      if (data.parent !== null) {
        this.showLendersDropdown = false;
      } else if (data.children && data.children.length > 0) {
        this.lendersList = [
      { id: data.currentOrganization.id, name: data.currentOrganization.name },
      ...data.children.map((child: any) => ({ id: child.id, name: child.name }))
    ];
        this.originalLendersList = [...this.lendersList];
        this.hasNextPageLenders = false; 
        this.showLendersDropdown = true; 
      } else {
        this.showLendersDropdown = false;
      }
    });
  } else if (role !== Role.Lender && role !== Role.LenderReadOnly) {
    this.showLendersDropdown = true;
  }
});

  @Input() imageProfile: string;
  @Input() orgName = '';
  @Output() openPreferences: EventEmitter<boolean> = new EventEmitter();
  @Output() openProfile: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private authService: AuthenticationService,
    public notificationService: NotificationService,
    private router: Router,
    private routeReuseStrategy: RouteReuseStrategy,
    private userService: UsersService,
    private searchService: SearchService,
    private statesService: StatesService,
    private providerService: ProviderOrganizationsService,
    private administratorService: AdministratorService,
    private industriesService: IndustriesService,
    private productsService: ProductsService,
    private lendersService: LenderOrganizationsService,
    private consultantsService: ConsultantsService,
    private bulkDownloadService: BulkDownloadService,
  ) {
    this.user$ = authService.user$;
    this.userProfile$ = userService.userProfile$;
  }

  private searchDataLoaded = false;

  ngOnInit(): void {
    this.userService.userRole$.subscribe((role) => {
      this.userRole = role;
    });

    this.searchService.hasFormsManagementAccess$.subscribe((hasAccess) => {
      this.hasFormsManagementAccess = hasAccess;
    });

    this.fetchNotifications();
    this.getNotification1MinInterval();
    this.reloadNotifications();

    this.productsService.cacheInvalidated$.subscribe(() => {
      if (this.searchDataLoaded) {
        this.fetchProducts();
      }
    });
  }

  onSearchOpened() {
    if (this.searchDataLoaded) return;
    this.searchDataLoaded = true;

    if (this.userRole !== Role.Lender && this.userRole !== Role.LenderReadOnly) {
      this.fetchLenders();
    }
    if (this.userRole !== Role.Provider && this.userRole !== Role.ProviderReadOnly && this.userRole !== Role.Consultant) {
      this.fetchProviders();
    }

    this.fetchStates();
    this.fetchAdministrators();
    this.fetchIndustries();
    this.fetchProducts();
  }

  private fetchStates() {
    this.statesService.get().subscribe((response) => {
      if (!response.isError) {
        this.statesList = response.data;
      }
    });
  }

  private fetchProviders() {
    this.providerService
      .get(
        {
          page: this.currentPageProviders,
          pageSize: this.pageSize,
          isActive: true
        })
      .subscribe((response: any) => {
      if (!response.isError) {
        this.providerList = response.data.items;
        this.providerList.unshift({ id: 0, name: 'All' });
      }
    });
  }
  private fetchAdministrators() {
    this.administratorService
    .get(
      {
        page: this.currentPageAdministrators,
        pageSize: this.pageSize,
        sortOrder: 'asc',
        sortColumn: 'name',
        isActive: true
      }).subscribe((response: any) => {
      if (!response.isError) {
        this.administratorList = response.data.items;
        this.administratorList.unshift({ id: 0, name: 'All' });
      }
    });
  }
  private fetchIndustries() {
    this.industriesService.get().subscribe((response) => {
      if (!response.isError) {
        this.industryList = response.data.filter((i: any) => i.id !== 0);
      }
    });
  }
  private fetchProducts() {
    this.productsService.get().subscribe((response: any) => {
      if (!response.isError) {
        this.productList = response.data.items;
      }
    });
  }
  private fetchLenders() {
    this.lendersService.get(
      {
        page: this.currentPageLenders,
        pageSize: this.pageSize,
        isActive: true
      }
    ).subscribe((response: any) => {
      if (!response.isError) {
        this.lendersList = response.data.items;
      }
    });
  }

  onLogout() {
    this.authService.logout();
  }

  getFullName(user: IUserProfile | null | undefined): string {
    let fullName = '';
    if (user?.firstName && user.lastName) {
      fullName = `${user.firstName} ${user.lastName}`;
    }
    return fullName;
  }

  navigateItemTo(event: { id?: number; typeOfResult?: TypeOfResult }) {
    if (event.typeOfResult == TypeOfResult.FormSubmission) {
      this.onViewFormSubmission(event.id);
    } else if (event.typeOfResult == TypeOfResult.FormConsolidation) {
      this.onViewFormConsolidation(event.id);
    } else if (event.typeOfResult == TypeOfResult.MBRRequest) {
      this.onViewMBR(event.id);
    } else if (event.typeOfResult == TypeOfResult.LenderApproval) {
      this.onViewLenderApproval(event.id);
    } else {
      this.onViewMarketingMaterial(event.id);
    }
  }

  markAsReadAndNavigate(
    notificationId?: number,
    referenceId?: number,
    entity?: TypeOfResult,
    documentTypeId?: number
  ) {
    this.notificationService.markAsRead([notificationId]).subscribe((_) => {
      this.notificationService.reloadBellNotifications.next(true);

      if (entity === TypeOfResult.BulkDownloadJob) {
        this.bulkDownloadService.downloadIfReady(referenceId);
        return;
      }

      const url = this.notificationService.navigate(entity, referenceId, documentTypeId);
      const defaultReuseStrategy = this.routeReuseStrategy.shouldReuseRoute.bind(this.routeReuseStrategy);
      this.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.navigateByUrl(url).then(() => {
        this.routeReuseStrategy.shouldReuseRoute = defaultReuseStrategy;
      });
    });
  }

  onViewFormSubmission(id?: number) {
    if (this.userRole === Role.Provider || this.userRole === Role.Consultant || this.userRole === Role.ProviderReadOnly) {
      this.router.navigate([`/provider/form-submission/details/${id}`]);
    } else if (this.userRole === Role.Lender || this.userRole === Role.LenderReadOnly) {
      this.router.navigate([`/lender/form-submission/details/${id}`]);
    } else {
      this.router.navigate([`/admin/form-submission/details/${id}`]);
    }
  }

  onViewMBR(id?: number) {
    if (this.userRole === Role.Lender || this.userRole === Role.LenderReadOnly) {
      this.router.navigate([`/lender/mbr-request/details/${id}`]);
    } else {
      this.router.navigate([`/admin/mbr-request/details/${id}`]);
    }
  }

  onViewLenderApproval(id?: number) {
    if (this.userRole === Role.Lender || this.userRole === Role.LenderReadOnly) {
      this.router.navigate([`/lender/lender-approval/details/${id}`]);
    } else if (this.userRole === Role.Provider || this.userRole === Role.Consultant || this.userRole === Role.ProviderReadOnly) {
      this.router.navigate([`/provider/lender-approval/details/${id}`]);
    } else {
      this.router.navigate([`/admin/lender-approval/details/${id}`]);
    }
  }

  onViewFormConsolidation(id?: number) {
    if (this.userRole === Role.Provider || this.userRole === Role.Consultant || this.userRole === Role.ProviderReadOnly) {
      this.router.navigate([`/provider/form-consolidation/details/${id}`]);
    } else {
      this.router.navigate([`/admin/form-consolidation/details/${id}`]);
    }
  }

  onViewMarketingMaterial(id?: number) {
    if (this.userRole === Role.Provider || this.userRole === Role.Consultant || this.userRole === Role.ProviderReadOnly) {
      this.router.navigate([`/provider/marketing-material/details/${id}`]);
    } else if (this.userRole === Role.Lender || this.userRole === Role.LenderReadOnly) {
      this.router.navigate([`/lender/marketing-material/details/${id}`]);
    } else {
      this.router.navigate([`/admin/marketing-material/details/${id}`]);
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onNotificationPreferences() {
    this.openPreferences.emit(true);
  }
  onProfile() {
    this.openProfile.emit(true);
  }

  private reloadNotifications() {
    this.notificationService.reloadBellNotifications.subscribe(() => {
      this.fetchNotifications();
    });
  }

  private getNotification1MinInterval() {
    this.subscription = interval(60000)
      .pipe(switchMap(() => this.notificationService.getLimited()))
      .subscribe((response: any) => {
        if (!response.isError) {
          this.notifications = response.data.items;
        }
      });
  }

  private fetchNotifications() {
    this.notificationService.getLimited().subscribe((response: any) => {
      if (!response.isError) {
        this.notifications = response.data.items;
      }
    });
  }

  applyFilter() {
    this.filterGroup.controls['searchKey'].setValue(
      this.filterGroup.controls['searchKey'].value
    );
  }

  onLoadMoreLenders(event: any) {
    const { searchKey, reset } = event;
    if (this.userRole === Role.Lender || this.userRole === Role.LenderReadOnly) {
    if (searchKey && searchKey.trim() !== '') {
      if (!this.originalLendersList) {
        this.originalLendersList = [...this.lendersList];
      }
        this.lendersList = this.originalLendersList.filter(lender => 
        lender.name.toLowerCase().includes(searchKey.toLowerCase())
      );
    } else {
      if (this.originalLendersList) {
        this.lendersList = [...this.originalLendersList];
      }
    }
      return;
    }

    this.lendersService.get(
      {
        page: reset ? 1 : this.currentPageLenders + 1,
        pageSize: this.pageSize,
        isActive: true,
        searchKey: searchKey
      }
    ).subscribe((response: any) => {
      const newItems = response.data.items.map((lender: any) => {
        return {
          id: lender.id,
          name: lender.name,
        };
      });

      if(reset) {
        this.lendersList = newItems;
      } else {
        this.lendersList = [
          ...this.lendersList,
          ...newItems
        ];
      }
      this.hasNextPageLenders = response.data.hasNextPage;
      this.currentPageLenders = response.data.page;
    });
  }

  onLoadMoreProviders() {
  // Only allow pagination for non-Provider users
  if (this.userRole === Role.Provider || this.userRole === Role.Consultant) {
    return;
  }

  this.providerService.get(
    {
      page: this.currentPageProviders + 1,
      pageSize: this.pageSize,
      isActive: true
    }
  ).subscribe((response: any) => {
    const newItems = response.data.items.map((provider: any) => {
      return {
        id: provider.id,
        name: provider.name,
      };
    });

    this.providerList = [
      ...this.providerList,
      ...newItems
    ];

    this.hasNextPageProviders = response.data.hasNextPage;
    this.currentPageProviders = response.data.page;
  });
}

  onLoadMoreAdministrators() {
    this.administratorService.get(
      {
        page: this.currentPageAdministrators + 1,
        pageSize: this.pageSize,
        sortColumn: 'name',
        isActive: true
      }
    ).subscribe((response: any) => {
      const newItems = response.data.items.map((administrator: any) => {
        return {
          id: administrator.id,
          name: administrator.name,
        };
      });

      this.administratorList = [
        ...this.administratorList,
        ...newItems
      ];

      this.hasNextPageAdministrators = response.data.hasNextPage;
      this.currentPageAdministrators = response.data.page;
    });
  }

  onEnterSearch(){}

  onResetPassword() {
    this.userService.resetUserPassword().subscribe();
  }
}
